import React, { useState, useEffect, useContext, useCallback } from "react";
import { MyContext } from "../../App";
import { fetchDataFromApi } from "../../utils/api";
import { putData } from "../../utils/api";
import io from "socket.io-client";
import "./orderlist.css";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Skeleton,
  TablePagination,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Fab,
  Snackbar,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  Notifications as NotificationsIcon,
  WifiOff as WifiOffIcon,
  Wifi as WifiIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";

// PDF generation utility functions
const generatePDF = (order) => {
  const doc = `
    <html>
      <head>
        <title>Order Invoice</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; color: #333; line-height: 1.6; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #007bff; padding-bottom: 20px; }
          .company-name { font-size: 28px; font-weight: bold; color: #007bff; margin-bottom: 5px; }
          .invoice-title { font-size: 24px; color: #666; margin: 10px 0; }
          .order-info { display: flex; justify-content: space-between; margin: 20px 0; background: #f8f9fa; padding: 15px; border-radius: 5px; }
          .info-section h3 { color: #007bff; margin-bottom: 10px; font-size: 16px; }
          .info-item { margin: 5px 0; font-size: 14px; }
          .status-chip { display: inline-block; padding: 4px 12px; border-radius: 15px; font-size: 12px; font-weight: bold; text-transform: uppercase; }
          .status-delivered { background: #d4edda; color: #155724; }
          .status-shipped { background: #cce7ff; color: #0056b3; }
          .status-processing { background: #fff3cd; color: #856404; }
          .status-pending { background: #f8d7da; color: #721c24; }
          .status-cancelled { background: #f8d7da; color: #721c24; }
          .items-table { width: 100%; border-collapse: collapse; margin: 20px 0; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
          .items-table th, .items-table td { border: 1px solid #ddd; padding: 12px; text-align: left; }
          .items-table th { background-color: #007bff; color: white; font-weight: bold; }
          .items-table tr:nth-child(even) { background-color: #f9f9f9; }
          .total-section { text-align: right; margin: 20px 0; padding: 15px; background: #f8f9fa; border-radius: 5px; }
          .total-row { margin: 5px 0; font-size: 16px; }
          .final-total { font-size: 20px; font-weight: bold; color: #007bff; border-top: 2px solid #007bff; padding-top: 10px; margin-top: 10px; }
          .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px; }
          .thank-you { font-size: 18px; color: #007bff; margin: 20px 0; }
          @media print { body { margin: 20px; } .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name"><img src="">BoxDekho</div>
          <div class="invoice-title">ORDER Bill</div>
          <div>Order #${(order._id || order.id || "").slice(-8).toUpperCase()}</div>
        </div>
        <div class="order-info">
          <div class="info-section">
            <h3>Order Details</h3>
            <div class="info-item"><strong>Order ID:</strong> #${(order._id || order.id || "").slice(-8).toUpperCase()}</div>
            <div class="info-item"><strong>Payment ID:</strong> ${order.paymentId || "Pending"}</div>
            <div class="info-item"><strong>Order Date:</strong> ${new Date(order.date || order.orderDate || order.createdAt).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
            <div class="info-item">
              <strong>Status:</strong> 
              <span class="status-chip status-${(order.orderStatus || order.status || "pending").toLowerCase()}">
                ${getStatusDisplayText(order.orderStatus || order.status)}
              </span>
            </div>
          </div>
          <div class="info-section">
            <h3>Billing Information</h3>
            <div class="info-item"><strong>Name:</strong> ${order.name || order.shippingAddress?.fullName || "N/A"}</div>
            <div class="info-item"><strong>Email:</strong> ${order.email || "N/A"}</div>
            <div class="info-item"><strong>Phone:</strong> ${order.phoneNumber || order.shippingAddress?.phoneNumber || "N/A"}</div>
          </div>
          <div class="info-section">
            <h3>Shipping Address</h3>
            <div class="info-item">
              ${order.address || order.shippingAddress?.streetAddressLine1 || "N/A"}<br>
              ${order.shippingAddress?.streetAddressLine2 ? order.shippingAddress.streetAddressLine2 + "<br>" : ""}
              ${order.shippingAddress?.city || "N/A"}, ${order.shippingAddress?.state || "N/A"}<br>
              ${order.shippingAddress?.country || "N/A"} - ${order.pincode || order.shippingAddress?.zipCode || "N/A"}
            </div>
          </div>
        </div>
        <table class="items-table">
          <thead>
            <tr>
              <th>Product Name</th>
              <th>Size</th>
              <th>Quantity</th>
              <th>Unit Price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            ${(order.items || order.products || []).map(
    (item) => `
              <tr>
                <td>${item.productTitle || item.productName || item.name || "Unknown Product"}</td>
                <td>${item.selectedSize || "N/A"}</td>
                <td>${item.quantity}</td>
                <td>₹${parseFloat(item.price).toFixed(2)}</td>
                <td>₹${(parseFloat(item.price) * parseInt(item.quantity)).toFixed(2)}</td>
              </tr>
            `
  ).join("")}
          </tbody>
        </table>
        <div class="total-section">
          <div class="total-row">Subtotal: ₹${calculateSubtotal(order.items || order.products || [])}</div>
          <div class="total-row">Tax & Fees: ₹${calculateTaxAndFees(order)}</div>
          <div class="total-row final-total">
            Total Amount: ₹${(order.amount || order.orderDetails?.total || order.total || order.totalAmount || 0).toFixed(2)}
          </div>
        </div>
        <div class="thank-you">Thank you for your business!</div>
        <div class="footer">
          <p>This is a computer-generated invoice. No signature required.</p>
          <p>Generated on: ${new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
        </div>
      </body>
    </html>
  `;
  return doc;
};
const getStatusDisplayText = (status) => {
  const statusLower = status?.toLowerCase() || "pending";
  switch (statusLower) {
    case "delivered":
    case "completed":
      return "Delivered";
    case "shipped":
    case "shipping":
      return "Shipped";
    case "cancelled":
    case "canceled":
      return "Cancelled";
    case "processing":
      return "Processing";
    case "confirmed":
      return "Confirmed";
    case "pending":
    default:
      return "Pending";
  }
};
const calculateSubtotal = (items) => {
  return items
    .reduce((sum, item) => {
      return sum + parseFloat(item.price) * parseInt(item.quantity);
    }, 0)
    .toFixed(2);
};
const calculateTaxAndFees = (order) => {
  const total = parseFloat(
    order.amount ||
    order.orderDetails?.total ||
    order.total ||
    order.totalAmount ||
    0
  );
  const subtotal = parseFloat(
    calculateSubtotal(order.items || order.products || [])
  );
  return (total - subtotal).toFixed(2);
};
const downloadPDF = (order) => {
  const htmlContent = generatePDF(order);

  const printWindow = window.open("", "_blank");
  printWindow.document.write(htmlContent);
  printWindow.document.close();

  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();

    setTimeout(() => {
      printWindow.close();
    }, 1000);
  };
};

const OrderList = () => {
  const context = useContext(MyContext);
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [statusUpdateCount, setStatusUpdateCount] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [realtimeUpdate, setRealtimeUpdate] = useState(null);
  const [showRealtimeSnackbar, setShowRealtimeSnackbar] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState({});

  const currentUser = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    if (currentUser?.userId) {
      const newSocket = io(
        process.env.REACT_APP_API_URL || "http://localhost:3001"
      );
      setSocket(newSocket);

      newSocket.on("connect", () => {
        setIsConnected(true);
        newSocket.emit("join_user_room", currentUser.userId);
      });

      newSocket.on("disconnect", () => {
        setIsConnected(false);
      });

      newSocket.on("connect_error", () => {
        setIsConnected(false);
      });

      newSocket.on("order_status_updated", (data) => {
        setOrders((prevOrders) =>
          prevOrders.map((order) =>
            order._id === data.orderId
              ? { ...order, orderStatus: data.newStatus }
              : order
          )
        );
        setRealtimeUpdate({
          orderId: data.orderId,
          newStatus: data.newStatus,
          message: data.message || `Order status updated to: ${data.newStatus}`,
        });
        setShowRealtimeSnackbar(true);
        setStatusUpdateCount((prev) => prev + 1);
        setLastUpdated(new Date());
      });

      newSocket.on("test_notification", (data) => {
        if (context?.setAlertBox) {
          context.setAlertBox({
            open: true,
            error: false,
            msg: data.message,
          });
        }
      });

      return () => {
        newSocket.disconnect();
      };
    }
  }, [currentUser?.userId, context]);

  const fetchOrders = useCallback(
    async (showLoading = true) => {
      if (!currentUser?.userId) {
        if (context?.setAlertBox) {
          context.setAlertBox({
            open: true,
            error: true,
            msg: "Please login to view your orders!",
          });
        }
        return;
      }

      if (showLoading) setIsLoading(true);
      setIsRefreshing(true);

      try {
        const response = await fetchDataFromApi(
          `/api/orders/user/${currentUser.userId}`
        );

        // Fix: handle null/undefined response and always treat as array
        let orderData = [];
        if (response && Array.isArray(response)) {
          orderData = response;
        } else if (response && Array.isArray(response.orderList)) {
          orderData = response.orderList;
        } else if (
          response &&
          response.success &&
          Array.isArray(response.result)
        ) {
          orderData = response.result;
        } else if (response && response.success && Array.isArray(response.orders)) {
          orderData = response.orders;
        } else {
          orderData = [];
        }

        const sortedOrders = orderData.sort((a, b) => {
          const dateA = new Date(
            a.date || a.orderDate || a.createdAt || a.dateCreated
          );
          const dateB = new Date(
            b.date || b.orderDate || b.createdAt || b.dateCreated
          );
          return dateB - dateA;
        });

        setOrders(sortedOrders);
        setLastUpdated(new Date());
      } catch (error) {
        if (context?.setAlertBox) {
          context.setAlertBox({
            open: true,
            error: true,
            msg: "Failed to load orders. Please try again.",
          });
        }
      } finally {
        if (showLoading) setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [currentUser, context]
  );

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleManualRefresh = () => {
    fetchOrders(false);
  };

  const handleCancelOrder = async (order) => {
    if (!window.confirm("Are you sure you want to cancel this order?")) return;

    try {
      const response = await putData(
        `/api/orders/${order._id}/status`,
        { status: "cancelled" }
      );
      if (response && response.success) {
        setOrders((prevOrders) =>
          prevOrders.map((o) =>
            o._id === order._id ? { ...o, orderStatus: "cancelled" } : o
          )
        );
        if (context?.setAlertBox) {
          context.setAlertBox({
            open: true,
            error: false,
            msg: "Order cancelled successfully!",
          });
        }
      } else {
        throw new Error((response && response.message) || "Failed to cancel order");
      }
    } catch (error) {
      if (context?.setAlertBox) {
        context.setAlertBox({
          open: true,
          error: true,
          msg: error.message || "Could not cancel order.",
        });
      }
    }
  };
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseRealtimeSnackbar = () => {
    setShowRealtimeSnackbar(false);
    setRealtimeUpdate(null);
  };

  const handleGeneratePDF = async (order) => {
    const orderId = order._id || order.id;

    try {
      setPdfGenerating((prev) => ({ ...prev, [orderId]: true }));

      await new Promise((resolve) => setTimeout(resolve, 500));

      downloadPDF(order);

      if (context?.setAlertBox) {
        context.setAlertBox({
          open: true,
          error: false,
          msg: "PDF invoice generated successfully!",
        });
      }
    } catch (error) {
      if (context?.setAlertBox) {
        context.setAlertBox({
          open: true,
          error: true,
          msg: "Failed to generate PDF. Please try again.",
        });
      }
    } finally {
      setPdfGenerating((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const getStatusInfo = (status) => {
    const statusLower = status?.toLowerCase() || "pending";
    switch (statusLower) {
      case "delivered":
      case "completed":
        return {
          color: "success",
          icon: <CheckCircleIcon fontSize="small" />,
          displayText: "Delivered",
        };
      case "shipped":
      case "shipping":
        return {
          color: "info",
          icon: <ShippingIcon fontSize="small" />,
          displayText: "Shipped",
        };
      case "cancelled":
      case "canceled":
        return {
          color: "error",
          icon: <CancelIcon fontSize="small" />,
          displayText: "Cancelled",
        };
      case "processing":
        return {
          color: "warning",
          icon: <PendingIcon fontSize="small" />,
          displayText: "Processing",
        };
      case "confirmed":
        return {
          color: "primary",
          icon: <CheckCircleIcon fontSize="small" />,
          displayText: "Confirmed",
        };
      case "pending":
      default:
        return {
          color: "default",
          icon: <AssignmentIcon fontSize="small" />,
          displayText: "Pending",
        };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return "Invalid Date";
    }
  };

  const getProductNames = (order) => {
    const items = order.items || order.products || [];
    if (!Array.isArray(items)) return "N/A";
    return items
      .map(
        (item) =>
          item.productTitle ||
          item.productName ||
          item.name ||
          "Unknown Product"
      )
      .join(", ");
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedOrder(null);
  };

  const LoadingSkeleton = () => (
    <TableContainer component={Paper} className="order-table-container">
      <Table>
        <TableHead>
          <TableRow>
            {[
              "Order ID",
              "Payment ID",
              "Products",
              "Phone",
              "Address",
              "Pincode",
              "Total",
              "Email",
              "Status",
              "Date",
              "Actions",
            ].map((header) => (
              <TableCell key={header} className="table-header">
                <Skeleton variant="text" width="100%" height={24} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {[1, 2, 3, 4, 5].map((row) => (
            <TableRow key={row}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((cell) => (
                <TableCell key={cell}>
                  <Skeleton variant="text" width="100%" height={20} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (isLoading) {
    return (
      <div className="orderlist-container">
        <div className="orderlist-header d-flex align-items-center justify-content-between mb-0">
          <div></div>
          <IconButton
            onClick={() => fetchOrders(false)}
            sx={{
              backgroundColor: '#3bb77e',
              color: 'white',
              '&:hover': { backgroundColor: '#2ecc71', boxShadow: '0 4px 15px rgba(59, 183, 126, 0.4)' },
              width: '45px',
              height: '45px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
          >
            {isRefreshing ? <CircularProgress size={24} color="inherit" /> : <RefreshIcon />}
          </IconButton>
        </div>
        <LoadingSkeleton />
      </div>
    );
  }

  if (!orders || orders.length === 0) {
    return (
      <div className="orderlist-container">

        <div className="orderlist-header d-flex align-items-center justify-content-between mb-3 px-2">
          <div></div>
          <IconButton
            onClick={handleManualRefresh}
            sx={{
              backgroundColor: '#3bb77e',
              color: 'white',
              '&:hover': { backgroundColor: '#2ecc71', boxShadow: '0 4px 15px rgba(59, 183, 126, 0.4)' },
              width: '45px',
              height: '45px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
            title="Refresh Orders"
          >
            {isRefreshing ? <CircularProgress size={24} color="inherit" /> : <RefreshIcon />}
          </IconButton>
        </div>
        <Card className="no-orders-card">
          <CardContent className="no-orders-content">
            <AssignmentIcon className="no-orders-icon" />
            <Typography variant="h6" className="no-orders-title">
              No Orders Found
            </Typography>
            <Typography variant="body2" className="no-orders-text">
              You haven't placed any orders yet. Start shopping to see your
              orders here!
            </Typography>
          </CardContent>
        </Card>
      </div>
    );
  }

  const paginatedOrders = orders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div className="orderlist-container">
      <div className="orderlist-header d-flex align-items-center justify-content-between mb-3 px-2">
        <div></div>
        <IconButton
          onClick={handleManualRefresh}
          sx={{
            backgroundColor: '#3bb77e',
            color: 'white',
            '&:hover': { backgroundColor: '#2ecc71', boxShadow: '0 4px 15px rgba(59, 183, 126, 0.4)' },
            width: '45px',
            height: '45px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}
          title="Refresh Orders"
        >
          {isRefreshing ? <CircularProgress size={24} color="inherit" /> : <RefreshIcon />}
        </IconButton>
      </div>



      <TableContainer component={Paper} className="order-table-container">
        <Table className="order-table" aria-label="orders table">
          <TableHead>
            <TableRow>
              <TableCell className="table-header">Order ID</TableCell>
              <TableCell className="table-header">Payment ID</TableCell>
              <TableCell className="table-header">Products</TableCell>
              <TableCell className="table-header">Phone Number</TableCell>
              <TableCell className="table-header">Address</TableCell>
              <TableCell className="table-header">Pincode</TableCell>
              <TableCell className="table-header">Total Amount</TableCell>
              <TableCell className="table-header">Email</TableCell>
              <TableCell className="table-header">Status</TableCell>
              <TableCell className="table-header">Date</TableCell>
              <TableCell className="table-header">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedOrders.map((order) => {
              const statusInfo = getStatusInfo(order.orderStatus || order.status);
              const address = order.shippingAddress || {};
              const orderId = order._id || order.id;

              return (
                <TableRow key={orderId} className="order-row">
                  <TableCell className="order-cell">
                    <Typography variant="body2" className="order-id">
                      #{(orderId || "").slice(-8).toUpperCase()}
                    </Typography>
                  </TableCell>
                  <TableCell className="order-cell">
                    <Typography variant="body2" className="payment-id">
                      {order.paymentId
                        ? `#${order.paymentId.slice(-8)}`
                        : "Pending"}
                    </Typography>
                  </TableCell>
                  <TableCell className="order-cell">
                    <Tooltip title={getProductNames(order)}>
                      <Typography variant="body2" className="products-cell">
                        {getProductNames(order).length > 30
                          ? `${getProductNames(order).substring(0, 30)}...`
                          : getProductNames(order)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell className="order-cell">
                    <Typography variant="body2">
                      {order.phoneNumber || address.phoneNumber || "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell className="order-cell">
                    <Tooltip
                      title={`${address.streetAddressLine1 || order.address || ""
                        }, ${address.city || ""}, ${address.state || ""}`}
                    >
                      <Typography variant="body2" className="address-cell">
                        {address.city && address.state
                          ? `${address.city}, ${address.state}`
                          : order.address || "N/A"}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell className="order-cell">
                    <Typography variant="body2">
                      {order.pincode || address.zipCode || "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell className="order-cell">
                    <Typography variant="body2" className="total-amount">
                      ₹
                      {parseFloat(
                        order.amount ||
                        order.orderDetails?.total ||
                        order.total ||
                        order.totalAmount ||
                        0
                      ).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell className="order-cell">
                    <Typography variant="body2" className="email-cell">
                      {(order.email || order.userEmail || "N/A").length > 20
                        ? `${(
                          order.email ||
                          order.userEmail ||
                          "N/A"
                        ).substring(0, 20)}...`
                        : order.email || order.userEmail || "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell className="order-cell">
                    <Chip
                      icon={statusInfo.icon}
                      label={statusInfo.displayText}
                      color={statusInfo.color}
                      size="small"
                      className="status-chip"
                      variant={
                        order.orderStatus === "pending" ? "outlined" : "filled"
                      }
                    />
                  </TableCell>
                  <TableCell className="order-cell">
                    <Typography variant="body2" className="order-date">
                      {formatDate(
                        order.date || order.orderDate || order.createdAt
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell className="order-cell">
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip title="View Order Details">
                        <IconButton
                          onClick={() => handleViewOrder(order)}
                          className="action-button"
                          size="small"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download PDF Bill">
                        <IconButton
                          onClick={() => handleGeneratePDF(order)}
                          className="action-button"
                          size="small"
                          disabled={pdfGenerating[orderId]}
                          color="primary"
                        >
                          {pdfGenerating[orderId] ? (
                            <CircularProgress size={16} />
                          ) : (
                            <PdfIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      {(order.orderStatus !== "cancelled" &&
                        order.orderStatus !== "delivered") && (
                          <Tooltip title="Cancel Order">
                            <IconButton
                              onClick={() => handleCancelOrder(order)}
                              color="error"
                              size="small"
                              className="action-button"
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={orders.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        className="table-pagination"
      />

      <Snackbar
        open={showRealtimeSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseRealtimeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseRealtimeSnackbar}
          severity="success"
          variant="filled"
          icon={<NotificationsIcon />}
        >
          <strong>Order Updated!</strong>
          <br />
          {realtimeUpdate?.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        className="order-dialog"
      >
        <DialogTitle className="dialog-title">
          Order Details - #
          {(selectedOrder?._id || selectedOrder?.id || "")
            .slice(-8)
            .toUpperCase()}
        </DialogTitle>
        <DialogContent className="dialog-content">
          {selectedOrder && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card className="detail-card">
                  <CardContent>
                    <Typography variant="h6" className="detail-section-title">
                      Order Information
                    </Typography>
                    <Box className="detail-item">
                      <strong>Order ID:</strong> #
                      {(selectedOrder._id || selectedOrder.id || "")
                        .slice(-8)
                        .toUpperCase()}
                    </Box>
                    <Box className="detail-item">
                      <strong>Payment ID:</strong>{" "}
                      {selectedOrder.paymentId || "Pending"}
                    </Box>
                    <Box className="detail-item">
                      <strong>Status:</strong>
                      <Chip
                        icon={
                          getStatusInfo(
                            selectedOrder.orderStatus || selectedOrder.status
                          ).icon
                        }
                        label={
                          getStatusInfo(
                            selectedOrder.orderStatus || selectedOrder.status
                          ).displayText
                        }
                        color={
                          getStatusInfo(
                            selectedOrder.orderStatus || selectedOrder.status
                          ).color
                        }
                        size="small"
                        style={{ marginLeft: "8px" }}
                        variant={
                          selectedOrder.orderStatus === "pending"
                            ? "outlined"
                            : "filled"
                        }
                      />
                    </Box>
                    <Box className="detail-item">
                      <strong>Order Date:</strong>{" "}
                      {formatDate(
                        selectedOrder.date ||
                        selectedOrder.orderDate ||
                        selectedOrder.createdAt
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card className="detail-card">
                  <CardContent>
                    <Typography variant="h6" className="detail-section-title">
                      Contact & Shipping
                    </Typography>
                    <Box className="detail-item">
                      <strong>Name:</strong>{" "}
                      {selectedOrder.name ||
                        selectedOrder.shippingAddress?.fullName ||
                        "N/A"}
                    </Box>
                    <Box className="detail-item">
                      <strong>Email:</strong> {selectedOrder.email || "N/A"}
                    </Box>
                    <Box className="detail-item">
                      <strong>Phone:</strong>{" "}
                      {selectedOrder.phoneNumber ||
                        selectedOrder.shippingAddress?.phoneNumber ||
                        "N/A"}
                    </Box>
                    <Box className="detail-item">
                      <strong>Address:</strong>
                      <Box component="span" className="address-detail">
                        {selectedOrder.address ||
                          selectedOrder.shippingAddress?.streetAddressLine1 ||
                          "N/A"}
                        {selectedOrder.shippingAddress?.streetAddressLine2 &&
                          `, ${selectedOrder.shippingAddress.streetAddressLine2}`}
                        <br />
                        {selectedOrder.shippingAddress?.city || "N/A"},{" "}
                        {selectedOrder.shippingAddress?.state || "N/A"}
                        <br />
                        {selectedOrder.shippingAddress?.country || "N/A"} -{" "}
                        {selectedOrder.pincode ||
                          selectedOrder.shippingAddress?.zipCode ||
                          "N/A"}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card className="detail-card">
                  <CardContent>
                    <Typography variant="h6" className="detail-section-title">
                      Ordered Items
                    </Typography>
                    <div className="ordered-items">
                      {(
                        selectedOrder.items ||
                        selectedOrder.products ||
                        []
                      ).map((item, index) => (
                        <div key={index} className="ordered-item">
                          <div className="item-info">
                            <Typography
                              variant="subtitle2"
                              className="item-name"
                            >
                              {item.productTitle ||
                                item.productName ||
                                item.name ||
                                "Unknown Product"}
                            </Typography>
                            <Typography
                              variant="body2"
                              className="item-details"
                            >
                              Quantity: {item.quantity} × ₹{item.price}
                              {item.selectedSize && (
                                <span> | Size: {item.selectedSize}</span>
                              )}
                            </Typography>
                          </div>
                          <Typography
                            variant="subtitle2"
                            className="item-total"
                          >
                            ₹
                            {(
                              parseFloat(item.price) * parseInt(item.quantity)
                            ).toFixed(2)}
                          </Typography>
                        </div>
                      ))}
                    </div>
                    <div className="order-total-section">
                      <div className="total-row final-total">
                        <span>
                          <strong>Total Amount:</strong>
                        </span>
                        <span>
                          <strong>
                            ₹
                            {(
                              selectedOrder.amount ||
                              selectedOrder.orderDetails?.total ||
                              selectedOrder.total ||
                              selectedOrder.totalAmount ||
                              0
                            ).toFixed(2)}
                          </strong>
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions className="dialog-actions">
          <Button
            onClick={() => handleGeneratePDF(selectedOrder)}
            color="primary"
            variant="outlined"
            startIcon={
              pdfGenerating[selectedOrder?._id || selectedOrder?.id] ? (
                <CircularProgress size={16} />
              ) : (
                <DownloadIcon />
              )
            }
            disabled={pdfGenerating[selectedOrder?._id || selectedOrder?.id]}
          >
            Download PDF Bill
          </Button>
          <Button
            onClick={handleCloseDialog}
            color="primary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OrderList;